import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Upload } from 'lucide-react';
import { adminApi, DownloadSettings, Inquiry, NewsArticle, Registration } from '@/lib/cms';

type Tab = 'news' | 'registrations' | 'inquiries' | 'downloads';
type SubmissionTab = 'registrations' | 'inquiries';
type DateFilter = {
  startDate: string;
  endDate: string;
};

const emptyArticle: Partial<NewsArticle> = {
  category: '公司新闻',
  date: new Date().toISOString().slice(0, 10),
  title: '',
  excerpt: '',
  image: '',
  author: '集团新闻中心',
  authorTitle: '蚂蚁链控股集团',
  readTime: '5 分钟',
  body: [''],
  tags: [],
  status: 'draft',
};

const statusLabels = {
  new: '新提交',
  contacted: '已联系',
  closed: '已关闭',
};

const emptyDownloadSettings: DownloadSettings = {
  iosAppStoreUrl: '',
  androidApkUrl: '',
  androidApkPath: '',
  androidApkName: '',
  androidApkSize: 0,
  wechatQrUrl: '',
  wechatQrPath: '',
  updatedAt: '',
};

export default function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState<Tab>('news');
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [downloadSettings, setDownloadSettings] = useState<DownloadSettings>(emptyDownloadSettings);
  const [editing, setEditing] = useState<Partial<NewsArticle> | null>(null);
  const [message, setMessage] = useState('');
  const [dateFilters, setDateFilters] = useState<Record<SubmissionTab, DateFilter>>({
    registrations: { startDate: '', endDate: '' },
    inquiries: { startDate: '', endDate: '' },
  });

  const counts = useMemo(() => ({
    news: news.length,
    registrations: registrations.filter((item) => item.status === 'new').length,
    inquiries: inquiries.filter((item) => item.status === 'new').length,
  }), [news, registrations, inquiries]);

  const filteredRegistrations = useMemo(
    () => filterAndSortByDate(registrations, dateFilters.registrations),
    [registrations, dateFilters.registrations],
  );
  const filteredInquiries = useMemo(
    () => filterAndSortByDate(inquiries, dateFilters.inquiries),
    [inquiries, dateFilters.inquiries],
  );

  useEffect(() => {
    Promise.allSettled([adminApi.setupStatus(), adminApi.me()])
      .then(async ([setupResult, meResult]) => {
        const setupNeeded = setupResult.status === 'fulfilled' && setupResult.value.needsSetup;
        setNeedsSetup(setupNeeded);
        if (meResult.status === 'fulfilled') {
          setLoggedIn(true);
          await loadAll();
        } else {
          setLoggedIn(false);
        }
      })
      .finally(() => setChecking(false));
  }, []);

  async function loadAll() {
    const [newsRes, regRes, inquiryRes, downloadRes] = await Promise.all([
      adminApi.news(),
      adminApi.registrations(),
      adminApi.inquiries(),
      adminApi.downloadSettings(),
    ]);
    setNews(newsRes.items);
    setRegistrations(regRes.items);
    setInquiries(inquiryRes.items);
    setDownloadSettings(downloadRes.item);
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setLoginError('');
    try {
      await adminApi.login(username, password);
      setLoggedIn(true);
      await loadAll();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : '登录失败');
    }
  }

  async function handleSetup(e: FormEvent) {
    e.preventDefault();
    setLoginError('');
    try {
      await adminApi.setup(username, password);
      setNeedsSetup(false);
      setLoggedIn(true);
      await loadAll();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : '初始化失败');
    }
  }

  async function handleLogout() {
    await adminApi.logout();
    setLoggedIn(false);
  }

  async function saveArticle(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    const payload = normalizeArticle(editing);
    const result = payload.id
      ? await adminApi.updateNews(payload.id, payload)
      : await adminApi.createNews(payload);
    setMessage(`已保存：${result.item.title}`);
    setEditing(null);
    await loadAll();
  }

  async function deleteArticle(id: number) {
    if (!window.confirm('确认删除这条新闻？')) return;
    await adminApi.deleteNews(id);
    setMessage('新闻已删除');
    await loadAll();
  }

  async function updateRegistrationStatus(id: number, status: Registration['status']) {
    await adminApi.updateRegistration(id, status);
    await loadAll();
  }

  async function updateRegistrationNote(id: number, adminNote: string) {
    await adminApi.updateRegistrationNote(id, adminNote);
    setMessage('报名备注已保存');
    await loadAll();
  }

  async function updateInquiryStatus(id: number, status: Inquiry['status']) {
    await adminApi.updateInquiry(id, status);
    await loadAll();
  }

  async function updateInquiryNote(id: number, adminNote: string) {
    await adminApi.updateInquiryNote(id, adminNote);
    setMessage('咨询备注已保存');
    await loadAll();
  }

  async function saveDownloadSettings(settings: DownloadSettings) {
    const result = await adminApi.updateDownloadSettings(settings);
    setDownloadSettings(result.item);
    setMessage('下载配置已保存');
  }

  async function uploadAndroidApk(file: File) {
    const upload = await adminApi.uploadApk(file);
    const nextSettings = {
      ...downloadSettings,
      androidApkUrl: upload.item.url,
      androidApkPath: upload.item.path,
      androidApkName: upload.item.name || file.name,
      androidApkSize: upload.item.size,
    };
    const result = await adminApi.updateDownloadSettings(nextSettings);
    setDownloadSettings(result.item);
    setMessage(`APK 已上传：${upload.item.name || file.name}`);
  }

  async function uploadWechatQr(file: File) {
    const upload = await adminApi.uploadImage(file);
    const nextSettings = {
      ...downloadSettings,
      wechatQrUrl: upload.item.url,
      wechatQrPath: upload.item.path,
    };
    const result = await adminApi.updateDownloadSettings(nextSettings);
    setDownloadSettings(result.item);
    setMessage('微信二维码已上传');
  }

  function updateDateFilter(tabName: SubmissionTab, filter: DateFilter) {
    setDateFilters((current) => ({ ...current, [tabName]: filter }));
  }

  if (checking) {
    return <AdminShell><div className="text-sm text-slate-500">正在检查登录状态...</div></AdminShell>;
  }

  if (!loggedIn) {
    return (
      <AdminShell>
        <form onSubmit={needsSetup ? handleSetup : handleLogin} className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">CMS Admin</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">{needsSetup ? '初始化管理员' : '管理后台登录'}</h1>
            {needsSetup && <p className="mt-2 text-sm leading-relaxed text-slate-500">首次使用，请创建管理员账号。创建成功后初始化入口会自动关闭。</p>}
          </div>
          <label className="mb-4 block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">账号</span>
            <input value={username} onChange={(e) => setUsername(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500" autoComplete="username" />
          </label>
          <label className="mb-5 block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">密码</span>
            <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500" autoComplete="current-password" />
          </label>
          {loginError && <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{loginError}</div>}
          <button className="w-full rounded-md bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">{needsSetup ? '创建管理员' : '登录'}</button>
        </form>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="w-full max-w-[1440px]">
        <header className="mb-6 flex flex-col justify-between gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">Ant Chain CMS</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">官网内容管理</h1>
          </div>
          <button onClick={handleLogout} className="inline-flex h-10 items-center justify-center rounded-md border border-slate-300 px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">退出</button>
        </header>

        <div className="mb-5 flex flex-wrap gap-2">
          <TabButton active={tab === 'news'} onClick={() => setTab('news')} label={`新闻 ${counts.news}`} />
          <TabButton active={tab === 'registrations'} onClick={() => setTab('registrations')} label={`报名 ${counts.registrations}`} />
          <TabButton active={tab === 'inquiries'} onClick={() => setTab('inquiries')} label={`咨询 ${counts.inquiries}`} />
          <TabButton active={tab === 'downloads'} onClick={() => setTab('downloads')} label="站点配置" />
        </div>

        {message && <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{message}</div>}

        {tab === 'news' && (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
            <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h2 className="font-semibold text-slate-900">新闻列表</h2>
                <button onClick={() => setEditing({ ...emptyArticle })} className="rounded-md bg-slate-950 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">新增新闻</button>
              </div>
              <div className="divide-y divide-slate-100">
                {news.map((item) => (
                  <article key={item.id} className="p-4">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{item.category}</span>
                      <span className="text-xs text-slate-500">{item.date}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${item.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{item.status === 'published' ? '已发布' : '草稿'}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-950">{item.title}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">{item.excerpt}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => setEditing(item)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">编辑</button>
                      <button onClick={() => deleteArticle(item.id)} className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">删除</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-4">
              {editing ? (
                <NewsEditor article={editing} setArticle={setEditing} onSubmit={saveArticle} onCancel={() => setEditing(null)} />
              ) : (
                <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-500">选择新闻编辑，或新增一条新闻</div>
              )}
            </section>
          </div>
        )}

        {tab === 'registrations' && (
          <DataTable
            title="产品报名"
            rows={filteredRegistrations}
            toolbar={
              <DateFilterControls
                value={dateFilters.registrations}
                total={registrations.length}
                filtered={filteredRegistrations.length}
                onChange={(filter) => updateDateFilter('registrations', filter)}
              />
            }
            render={(item) => (
              <SubmissionCard
                key={item.id}
                title={`${item.name} / ${item.company || '个人'}`}
                subtitle={`${item.phone} · ${item.email}`}
                status={item.status}
                createdAt={item.created_at}
                adminNote={item.admin_note}
                body={[
                  `意向产品：${item.products?.join('、') || '未选择'}`,
                  item.message ? `留言：${item.message}` : '',
                ]}
                onStatus={(status) => updateRegistrationStatus(item.id, status)}
                onNote={(adminNote) => updateRegistrationNote(item.id, adminNote)}
              />
            )}
          />
        )}

        {tab === 'inquiries' && (
          <DataTable
            title="联系咨询"
            rows={filteredInquiries}
            toolbar={
              <DateFilterControls
                value={dateFilters.inquiries}
                total={inquiries.length}
                filtered={filteredInquiries.length}
                onChange={(filter) => updateDateFilter('inquiries', filter)}
              />
            }
            render={(item) => (
              <SubmissionCard
                key={item.id}
                title={`${item.name} / ${item.company || '个人'}`}
                subtitle={`${item.cooperation_type} · ${item.phone || item.email}`}
                status={item.status}
                createdAt={item.created_at}
                adminNote={item.admin_note}
                body={[`邮箱：${item.email}`, `留言：${item.message}`]}
                onStatus={(status) => updateInquiryStatus(item.id, status)}
                onNote={(adminNote) => updateInquiryNote(item.id, adminNote)}
              />
            )}
          />
        )}

        {tab === 'downloads' && (
          <DownloadSettingsPanel
            settings={downloadSettings}
            onSave={saveDownloadSettings}
            onUploadApk={uploadAndroidApk}
            onUploadWechatQr={uploadWechatQr}
          />
        )}
      </div>
    </AdminShell>
  );
}

function AdminShell({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 text-slate-950 md:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-48px)] items-center justify-center">
        {children}
      </div>
    </main>
  );
}

function TabButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`rounded-md px-4 py-2 text-sm font-semibold ${active ? 'bg-slate-950 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}>
      {label}
    </button>
  );
}

function NewsEditor({ article, setArticle, onSubmit, onCancel }: {
  article: Partial<NewsArticle>;
  setArticle: (value: Partial<NewsArticle>) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}) {
  const bodyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  const bodyImageInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState<'cover' | 'body' | null>(null);
  const bodyText = (article.body || []).join('\n\n');
  const tagsText = (article.tags || []).join('，');

  function update<K extends keyof NewsArticle>(key: K, value: NewsArticle[K]) {
    setArticle({ ...article, [key]: value });
  }

  function setBodyFromText(value: string) {
    update('body', textToBody(value));
  }

  function insertBodyImage(url: string) {
    const marker = `IMAGE:${url}`;
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setBodyFromText([bodyText, marker].filter(Boolean).join('\n\n'));
      return;
    }
    const start = textarea.selectionStart ?? bodyText.length;
    const end = textarea.selectionEnd ?? bodyText.length;
    const before = bodyText.slice(0, start).replace(/\s*$/, '');
    const after = bodyText.slice(end).replace(/^\s*/, '');
    const next = [before, marker, after].filter(Boolean).join('\n\n');
    setBodyFromText(next);
    window.requestAnimationFrame(() => {
      const position = (before ? before.length + 2 : 0) + marker.length;
      textarea.focus();
      textarea.setSelectionRange(position, position);
    });
  }

  async function uploadImage(file: File, target: 'cover' | 'body') {
    setUploading(target);
    try {
      const result = await adminApi.uploadImage(file);
      if (target === 'cover') {
        update('image', result.item.url);
      } else {
        insertBodyImage(result.item.url);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '图片上传失败');
    } finally {
      setUploading(null);
    }
  }

  function handleUploadChange(e: ChangeEvent<HTMLInputElement>, target: 'cover' | 'body') {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void uploadImage(file, target);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">{article.id ? '编辑新闻' : '新增新闻'}</h2>
        <button type="button" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900">取消</button>
      </div>
      <Field label="标题" value={article.title || ''} onChange={(v) => update('title', v)} required />
      <div className="grid grid-cols-2 gap-3">
        <Field label="分类" value={article.category || ''} onChange={(v) => update('category', v)} required />
        <Field label="日期" value={article.date || ''} onChange={(v) => update('date', v)} required />
      </div>
      <Field label="摘要" value={article.excerpt || ''} onChange={(v) => update('excerpt', v)} required textarea />
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700">封面图片 URL</span>
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploading !== null}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading === 'cover' ? '上传中...' : '上传图片'}
          </button>
          <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUploadChange(e, 'cover')} />
        </div>
        <input value={article.image || ''} onChange={(e) => update('image', e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500" />
        {article.image && (
          <div className="mt-2 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
            <img src={article.image} alt="封面预览" className="h-28 w-full object-cover" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="作者" value={article.author || ''} onChange={(v) => update('author', v)} />
        <Field label="阅读时间" value={article.readTime || ''} onChange={(v) => update('readTime', v)} />
      </div>
      <Field label="作者头衔" value={article.authorTitle || ''} onChange={(v) => update('authorTitle', v)} />
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700">正文（段落之间空一行）</span>
          <button
            type="button"
            onClick={() => bodyImageInputRef.current?.click()}
            disabled={uploading !== null}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ImagePlus className="h-3.5 w-3.5" />
            {uploading === 'body' ? '上传中...' : '插入图片'}
          </button>
          <input ref={bodyImageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUploadChange(e, 'body')} />
        </div>
        <textarea
          ref={bodyTextareaRef}
          value={bodyText}
          rows={8}
          onChange={(e) => setBodyFromText(e.target.value)}
          className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
        />
        <p className="mt-1.5 text-xs text-slate-500">插图会以 IMAGE:/uploads/... 插入到光标位置，前台详情页会自动显示图片。</p>
      </div>
      <Field label="标签（用逗号分隔）" value={tagsText} onChange={(v) => update('tags', v.split(/[，,]/).map((tag) => tag.trim()).filter(Boolean))} />
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">状态</span>
        <select value={article.status || 'draft'} onChange={(e) => update('status', e.target.value as NewsArticle['status'])} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500">
          <option value="published">发布</option>
          <option value="draft">草稿</option>
        </select>
      </label>
      <button className="w-full rounded-md bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-500">保存新闻</button>
    </form>
  );
}

function DownloadSettingsPanel({ settings, onSave, onUploadApk, onUploadWechatQr }: {
  settings: DownloadSettings;
  onSave: (settings: DownloadSettings) => Promise<void>;
  onUploadApk: (file: File) => Promise<void>;
  onUploadWechatQr: (file: File) => Promise<void>;
}) {
  const apkInputRef = useRef<HTMLInputElement | null>(null);
  const qrInputRef = useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'apk' | 'qr' | null>(null);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(draft);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '保存下载配置失败');
    } finally {
      setSaving(false);
    }
  }

  async function handleApkChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      window.alert('请选择 .apk 文件');
      return;
    }
    setUploading('apk');
    try {
      await onUploadApk(file);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'APK 上传失败');
    } finally {
      setUploading(null);
    }
  }

  async function handleQrChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading('qr');
    try {
      await onUploadWechatQr(file);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '微信二维码上传失败');
    } finally {
      setUploading(null);
    }
  }

  return (
    <section className="max-w-3xl rounded-lg border border-slate-200 bg-white p-5">
      <div className="mb-5">
        <h2 className="font-semibold text-slate-900">站点配置</h2>
        <p className="mt-1 text-sm text-slate-500">配置前台下载按钮和报名成功后的客服微信二维码。</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="rounded-md border border-slate-200 p-4">
          <h3 className="mb-4 text-sm font-semibold text-slate-900">下载配置</h3>
        <Field
          label="App Store 链接"
          value={draft.iosAppStoreUrl}
          onChange={(value) => setDraft({ ...draft, iosAppStoreUrl: value })}
        />

        <div className="mt-5">
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-slate-700">Android APK</span>
            <button
              type="button"
              onClick={() => apkInputRef.current?.click()}
              disabled={uploading !== null}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading === 'apk' ? '上传中...' : '上传 APK'}
            </button>
            <input ref={apkInputRef} type="file" accept=".apk,application/vnd.android.package-archive" className="hidden" onChange={handleApkChange} />
          </div>
          <input
            value={draft.androidApkUrl}
            readOnly
            className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600 outline-none"
            placeholder="上传 APK 后自动生成下载地址"
          />
          {draft.androidApkUrl && (
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <p>文件：{draft.androidApkName || 'android.apk'}</p>
              <p>大小：{formatBytes(draft.androidApkSize)}</p>
              <a href={draft.androidApkUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-amber-700 hover:text-amber-600">测试下载链接</a>
            </div>
          )}
        </div>
        </div>

        <div className="rounded-md border border-slate-200 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-900">报名微信二维码</h3>
            <button
              type="button"
              onClick={() => qrInputRef.current?.click()}
              disabled={uploading !== null}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading === 'qr' ? '上传中...' : '上传二维码'}
            </button>
            <input ref={qrInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleQrChange} />
          </div>
          <input
            value={draft.wechatQrUrl}
            readOnly
            className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600 outline-none"
            placeholder="上传二维码后自动生成图片地址"
          />
          {draft.wechatQrUrl && (
            <div className="mt-3 flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <img src={draft.wechatQrUrl} alt="微信二维码预览" className="h-24 w-24 rounded-md border border-slate-200 object-cover" />
              <div className="text-xs text-slate-600">
                <p>报名成功弹窗会展示此二维码。</p>
                <a href={draft.wechatQrUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-amber-700 hover:text-amber-600">打开二维码链接</a>
              </div>
            </div>
          )}
        </div>

        <button disabled={saving || uploading !== null} className="w-full rounded-md bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60">
          {saving ? '保存中...' : '保存站点配置'}
        </button>
      </form>
    </section>
  );
}

function Field({ label, value, onChange, required, textarea, rows = 3 }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  textarea?: boolean;
  rows?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</span>
      {textarea ? (
        <textarea required={required} value={value} rows={rows} onChange={(e) => onChange(e.target.value)} className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500" />
      ) : (
        <input required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500" />
      )}
    </label>
  );
}

function formatBytes(value: number) {
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function normalizeArticle(article: Partial<NewsArticle>): Partial<NewsArticle> {
  return {
    ...article,
    body: (article.body || []).filter(Boolean),
    tags: (article.tags || []).filter(Boolean),
  };
}

function textToBody(value: string) {
  return value.split(/\n\s*\n/).map((line) => line.trim()).filter(Boolean);
}

function DataTable<T>({ title, rows, toolbar, render }: { title: string; rows: T[]; toolbar?: ReactNode; render: (row: T) => ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {toolbar}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.length ? rows.map(render) : <div className="p-8 text-center text-sm text-slate-500">暂无数据</div>}
      </div>
    </section>
  );
}

function DateFilterControls({ value, total, filtered, onChange }: {
  value: DateFilter;
  total: number;
  filtered: number;
  onChange: (value: DateFilter) => void;
}) {
  const hasFilter = Boolean(value.startDate || value.endDate);

  return (
    <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">开始日期</span>
          <input
            type="date"
            value={value.startDate}
            onChange={(e) => onChange({ ...value, startDate: e.target.value })}
            className="h-9 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-amber-500"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">结束日期</span>
          <input
            type="date"
            value={value.endDate}
            onChange={(e) => onChange({ ...value, endDate: e.target.value })}
            className="h-9 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-amber-500"
          />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{hasFilter ? `已筛选 ${filtered} / ${total}` : `共 ${total} 条`}</span>
        {hasFilter && (
          <button
            type="button"
            onClick={() => onChange({ startDate: '', endDate: '' })}
            className="h-9 rounded-md border border-slate-300 px-3 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            清除筛选
          </button>
        )}
      </div>
    </div>
  );
}

function SubmissionCard({ title, subtitle, status, createdAt, adminNote, body, onStatus, onNote }: {
  title: string;
  subtitle: string;
  status: Registration['status'];
  createdAt: string;
  adminNote: string;
  body: string[];
  onStatus: (status: Registration['status']) => void;
  onNote: (adminNote: string) => Promise<void>;
}) {
  const [noteDraft, setNoteDraft] = useState(adminNote || '');
  const [savingNote, setSavingNote] = useState(false);
  const noteChanged = noteDraft !== (adminNote || '');

  useEffect(() => {
    setNoteDraft(adminNote || '');
  }, [adminNote]);

  async function saveNote() {
    setSavingNote(true);
    try {
      await onNote(noteDraft);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '备注保存失败');
    } finally {
      setSavingNote(false);
    }
  }

  return (
    <article className="p-4">
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{statusLabels[status]}</span>
          </div>
          <p className="text-sm text-slate-500">{subtitle}</p>
          <p className="mt-1 text-xs font-medium text-slate-500">提交日期：{formatDateTime(createdAt)}</p>
          <div className="mt-3 space-y-1 text-sm text-slate-600">
            {body.filter(Boolean).map((line) => <p key={line}>{line}</p>)}
          </div>
          <div className="mt-4 max-w-2xl">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-500">运维备注</span>
              <textarea
                value={noteDraft}
                rows={2}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="记录联系情况、跟进结果等"
                className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
              />
            </label>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={saveNote}
                disabled={!noteChanged || savingNote}
                className="h-8 rounded-md border border-slate-300 px-3 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingNote ? '保存中...' : '保存备注'}
              </button>
              {noteChanged && <span className="text-xs text-amber-600">备注未保存</span>}
            </div>
          </div>
        </div>
        <select value={status} onChange={(e) => onStatus(e.target.value as Registration['status'])} className="h-9 rounded-md border border-slate-300 px-2 text-sm">
          <option value="new">新提交</option>
          <option value="contacted">已联系</option>
          <option value="closed">已关闭</option>
        </select>
      </div>
    </article>
  );
}

function filterAndSortByDate<T extends { created_at: string }>(items: T[], filter: DateFilter) {
  return [...items]
    .filter((item) => isWithinDateFilter(item.created_at, filter))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

function isWithinDateFilter(value: string, filter: DateFilter) {
  const date = value?.slice(0, 10);
  if (!date) return false;
  if (filter.startDate && date < filter.startDate) return false;
  if (filter.endDate && date > filter.endDate) return false;
  return true;
}

function formatDateTime(value: string) {
  return value ? value.replace('T', ' ').slice(0, 19) : '-';
}
